/**
 * Phase — show view on the shared Inspector grammar (one-grammar law).
 * Overview leads with sort order + the domain-specific start/terminal markers
 * and purpose; the phase context renders as markdown (shared `<Markdown>`) in a
 * Body section; Relations link the parent Process; the "Process Graph" jump is
 * kept as a Header action; ids/timestamps sink to a muted Technical tail; Audit
 * closes the page.
 */
import { useNavigation, useShow, type HttpError } from "@refinedev/core";
import { GitBranch, Workflow } from "@/components/ui/icon";

import type { Phase } from "@aos/shared";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Inspector,
  InspectorHeader,
  InspectorSection,
  Field,
  FieldGrid,
  RefLink,
  RelationsBlock,
  AuditBlock,
  ShowPageSkeleton,
  ShowPageError,
  TechnicalId,
} from "@/components/inspector";
import { Markdown } from "@/components/app";
import { formatDateTime } from "@/lib/format";

/** Phase detail view. */
export function PhaseShow() {
  const { query } = useShow<Phase, HttpError>({ resource: "phases" });
  const { data, isLoading, isError, error, refetch } = query;
  const phase = data?.data;
  const { push } = useNavigation();

  if (isLoading) return <ShowPageSkeleton />;
  if (isError || !phase) {
    return (
      <ShowPageError
        message={error?.message ?? "Phase not found."}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  return (
    <Inspector>
      <InspectorHeader
        title={phase.name}
        subtitle="A phase within a process — its purpose, context, and ordering."
        actions={
          <Button
            size="sm"
            variant="outline"
            onClick={() => push(`/processes/${phase.process_id}/graph`)}
          >
            <GitBranch className="mr-2 h-4 w-4" />
            Process Graph
          </Button>
        }
      />

      <InspectorSection title="Overview">
        <FieldGrid>
          <Field label="Sort order">{phase.sort_order}</Field>
          <Field label="Markers">
            {phase.is_start || phase.is_terminal ? (
              <span className="space-x-1">
                {phase.is_start ? (
                  <Badge variant="secondary">start</Badge>
                ) : null}
                {phase.is_terminal ? (
                  <Badge variant="secondary">terminal</Badge>
                ) : null}
              </span>
            ) : null}
          </Field>
          <Field label="Process">
            <RefLink resource="processes" id={phase.process_id} />
          </Field>
          {phase.purpose ? (
            <Field label="Purpose" full>
              {phase.purpose}
            </Field>
          ) : null}
        </FieldGrid>
      </InspectorSection>

      {phase.context_markdown && phase.context_markdown.trim() ? (
        <InspectorSection title="Context">
          <Markdown source={phase.context_markdown} />
        </InspectorSection>
      ) : null}

      <InspectorSection title="Relations">
        <RelationsBlock
          groups={[
            {
              title: "References",
              items: [
                {
                  key: "process",
                  label: "Process",
                  icon: Workflow,
                  resource: "processes",
                  id: phase.process_id,
                },
              ],
            },
          ]}
        />
      </InspectorSection>

      <InspectorSection title="Technical">
        <FieldGrid className="text-muted-foreground">
          <Field label="Id">
            <TechnicalId id={phase.id} />
          </Field>
          <Field label="Process">
            <RefLink resource="processes" id={phase.process_id} />
          </Field>
          <Field label="Created">{formatDateTime(phase.created_at)}</Field>
          <Field label="Updated">{formatDateTime(phase.updated_at)}</Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Audit">
        <AuditBlock entityType="phases" entityId={phase.id} />
      </InspectorSection>
    </Inspector>
  );
}

export default PhaseShow;
